/**
 * Domain Pack Progress Tracker
 * Shows real-time progress during AI research with phase indicators
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  TrendingUp,
  AlertTriangle,
  Award,
  Database,
  Lightbulb,
  CheckCircle,
  Loader2,
  ArrowRight,
  Clock,
} from 'lucide-react';

import { getResearchProgress, getDomainPack, triggerSubdomainResearch } from '../lib/api/domainPacksApi';
import type { ResearchProgress, ResearchPhase } from '../types/domainPack';
import { PHASE_NAMES } from '../types/domainPack';

// Phase configuration
const PHASE_CONFIG: Record<ResearchPhase, { icon: React.ComponentType<any>; color: string; description: string }> = {
  phase_1_discovery: {
    icon: Brain,
    color: 'text-blue-600',
    description: 'Analyzing domain and identifying key subdomains',
  },
  phase_2_trends: {
    icon: TrendingUp,
    color: 'text-green-600',
    description: 'Researching latest trends and market dynamics',
  },
  phase_3_issues: {
    icon: AlertTriangle,
    color: 'text-orange-600',
    description: 'Identifying common issues and pitfalls',
  },
  phase_4_practices: {
    icon: Award,
    color: 'text-purple-600',
    description: 'Discovering industry best practices',
  },
  phase_5_data_elements: {
    icon: Database,
    color: 'text-teal-600',
    description: 'Defining required data elements',
  },
  phase_6_hypotheses: {
    icon: Lightbulb,
    color: 'text-yellow-600',
    description: 'Generating testable hypotheses',
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-600',
    description: 'Research complete',
  },
};

const DomainPackProgress: React.FC = () => {
  const navigate = useNavigate();
  const { domainPackId } = useParams<{ domainPackId: string }>();
  const [searchParams] = useSearchParams();
  const subdomainId = searchParams.get('subdomain_id');
  const triggerResearch = searchParams.get('trigger_research') === 'true';
  const [jobId, setJobId] = useState<string | null>(searchParams.get('job_id'));
  const [isTriggering, setIsTriggering] = useState(false);

  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<number | null>(null);

  // Trigger research if needed
  useEffect(() => {
    if (triggerResearch && subdomainId && !jobId && !isTriggering) {
      setIsTriggering(true);
      triggerSubdomainResearch(domainPackId!, subdomainId)
        .then((response) => {
          setJobId(response.job_id);
          setIsTriggering(false);
        })
        .catch((error) => {
          console.error('Failed to trigger research:', error);
          setIsTriggering(false);
          // Navigate back on error
          navigate(`/domain-packs/${domainPackId}`);
        });
    }
  }, [triggerResearch, subdomainId, jobId, domainPackId, isTriggering, navigate]);

  // Poll for progress updates every 3 seconds
  const {
    data: progress,
    isLoading: progressLoading,
    error: progressError,
  } = useQuery({
    queryKey: ['domain-pack-progress', jobId],
    queryFn: () => getResearchProgress(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      // Stop polling when completed or if there are errors
      const data = query.state.data;
      if (data?.current_phase === 'completed' || data?.errors?.length) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    staleTime: 0, // Always fetch fresh data
  });

  // Fetch domain pack details to show names
  const {
    data: domainPack,
    isLoading: domainPackLoading,
  } = useQuery({
    queryKey: ['domain-pack', domainPackId],
    queryFn: () => getDomainPack(domainPackId!),
    enabled: !!domainPackId,
  });

  // Update estimated time remaining
  useEffect(() => {
    if (progress?.estimated_completion) {
      const completion = new Date(progress.estimated_completion);
      const now = new Date();
      const remaining = Math.max(0, Math.floor((completion.getTime() - now.getTime()) / 1000));
      setEstimatedTimeRemaining(remaining);
    }
  }, [progress]);

  // Countdown timer
  useEffect(() => {
    if (estimatedTimeRemaining === null || estimatedTimeRemaining <= 0) return;

    const timer = setInterval(() => {
      setEstimatedTimeRemaining(prev => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [estimatedTimeRemaining]);

  // Navigate to detail page when complete
  useEffect(() => {
    if (progress?.current_phase === 'completed' && domainPack) {
      // Delay slightly to show completion state
      const timer = setTimeout(() => {
        navigate(`/domain-packs/${domainPackId}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [progress, domainPack, domainPackId, navigate]);

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate overall progress percentage
  const getPhaseNumber = (phase: ResearchPhase): number => {
    const phases: ResearchPhase[] = [
      'phase_1_discovery',
      'phase_2_trends',
      'phase_3_issues',
      'phase_4_practices',
      'phase_5_data_elements',
      'phase_6_hypotheses',
      'completed',
    ];
    return phases.indexOf(phase);
  };

  const calculateOverallProgress = (): number => {
    if (!progress) return 0;

    // Use overall_progress from backend (0-1 range)
    // Backend correctly tracks progress across all phases
    return Math.min(100, Math.round(progress.overall_progress * 100));
  };

  // Show loading while triggering research or waiting for job ID
  if (!jobId || isTriggering) {
    if (triggerResearch && domainPackId) {
      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
            <p className="text-neutral-700 font-medium">Starting AI Research...</p>
            <p className="text-neutral-500 text-sm mt-1">Preparing research job</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Missing job ID or domain pack ID</p>
          <button
            onClick={() => navigate('/domain-packs')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  if (progressLoading || !progress) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-neutral-700 font-medium">Loading research progress...</p>
        </div>
      </div>
    );
  }

  if (progressError) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">
            Failed to Load Progress
          </h2>
          <p className="text-neutral-600 mb-6">
            Unable to retrieve research progress. The job may have failed or been cancelled.
          </p>
          <button
            onClick={() => navigate('/domain-packs')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  const overallProgress = calculateOverallProgress();
  const currentPhaseConfig = PHASE_CONFIG[progress.current_phase] || PHASE_CONFIG.phase_1_discovery;
  const CurrentPhaseIcon = currentPhaseConfig.icon;

  // Find the specific subdomain being researched
  const currentSubdomain = domainPack?.subdomains.find(
    (sub) => sub.subdomain_id === subdomainId
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                AI Research in Progress
              </h1>
              {domainPack && currentSubdomain ? (
                <p className="text-neutral-600 mt-1">
                  <span className="font-semibold text-neutral-800">{domainPack.domain_name}</span>
                  {' → '}
                  <span className="font-semibold text-primary-600">{currentSubdomain.name}</span>
                </p>
              ) : (
                <p className="text-neutral-600 mt-1">
                  Generating comprehensive domain pack
                </p>
              )}
            </div>
          </div>

          {/* Overall Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-neutral-700">
                Overall Progress
              </span>
              <span className="text-sm font-semibold text-primary-600">
                {overallProgress}%
              </span>
            </div>
            <div className="w-full h-3 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Time Estimate */}
          {estimatedTimeRemaining !== null && estimatedTimeRemaining > 0 && (
            <div className="mt-4 flex items-center gap-2 text-sm text-neutral-600">
              <Clock className="w-4 h-4" />
              <span>Estimated time remaining: {formatTimeRemaining(estimatedTimeRemaining)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Current Phase Status */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center ${currentPhaseConfig.color}`}>
              {progress.current_phase === 'completed' ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <CurrentPhaseIcon className="w-6 h-6 animate-pulse" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-neutral-900 mb-1">
                {PHASE_NAMES[progress.current_phase]}
              </h2>
              <p className="text-neutral-600 mb-3">
                {currentPhaseConfig.description}
              </p>
              <p className="text-sm text-neutral-700 bg-neutral-50 rounded px-3 py-2 border border-neutral-200">
                {progress.current_activity}
              </p>
            </div>
          </div>

          {/* Phase Progress Bar */}
          {progress.current_phase !== 'completed' && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700">
                  Phase Progress
                </span>
                <span className="text-sm font-semibold text-neutral-600">
                  {Math.round(progress.phase_progress * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-400 transition-all duration-300"
                  style={{ width: `${progress.phase_progress * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Phase Timeline */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">
            Research Phases
          </h3>
          <div className="space-y-4">
            {Object.entries(PHASE_CONFIG)
              .filter(([phase]) => phase !== 'completed')
              .map(([phase, config], index) => {
                const PhaseIcon = config.icon;
                const phaseKey = phase as ResearchPhase;
                const currentPhaseNum = getPhaseNumber(progress.current_phase);
                const thisPhaseNum = getPhaseNumber(phaseKey);

                const isCompleted = thisPhaseNum < currentPhaseNum;
                const isCurrent = phaseKey === progress.current_phase;
                const isPending = thisPhaseNum > currentPhaseNum;

                return (
                  <div
                    key={phase}
                    className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                      isCurrent
                        ? 'border-primary-300 bg-primary-50'
                        : isCompleted
                        ? 'border-green-200 bg-green-50'
                        : 'border-neutral-200 bg-neutral-50'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCurrent
                          ? 'bg-primary-600 text-white'
                          : isCompleted
                          ? 'bg-green-600 text-white'
                          : 'bg-neutral-300 text-neutral-600'
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : isCurrent ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <span className="text-sm font-semibold">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4
                        className={`font-semibold ${
                          isCurrent || isCompleted
                            ? 'text-neutral-900'
                            : 'text-neutral-500'
                        }`}
                      >
                        {PHASE_NAMES[phaseKey]}
                      </h4>
                      <p
                        className={`text-sm ${
                          isCurrent || isCompleted
                            ? 'text-neutral-600'
                            : 'text-neutral-500'
                        }`}
                      >
                        {config.description}
                      </p>
                    </div>
                    {isCompleted && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Errors */}
        {progress.errors.length > 0 && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Errors Encountered
            </h3>
            <ul className="space-y-2">
              {progress.errors.map((error, index) => (
                <li key={index} className="text-sm text-red-700">
                  • {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Completion Message */}
        {progress.current_phase === 'completed' && !progress.errors.length && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-900 mb-1">
                  Research Complete!
                </h3>
                <p className="text-sm text-green-700">
                  Redirecting to domain pack details...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainPackProgress;
